/**
 * Bookings: tables held for a party at a time. Enterprise (`floor.reservations`).
 *
 * Every date and time here is the site's own clock, worked out by the server:
 * a booking says `local_date` and `local_time` ("19:30"), and a new one is sent
 * as a date and a time in the same terms. No screen converts from the laptop's
 * zone, because the guest said "half seven" in the restaurant's.
 *
 * A deposit is a recorded amount — asked for, and marked received by whatever
 * means the restaurant took it. Nothing here takes a card.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type BookingStatus = 'booked' | 'confirmed' | 'seated' | 'no_show' | 'cancelled';
export type BookingSource = 'phone' | 'in_person' | 'email' | 'waitlist' | 'other';

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  booked: 'Booked',
  confirmed: 'Confirmed',
  seated: 'Seated',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

export const BOOKING_SOURCE_LABELS: Record<BookingSource, string> = {
  phone: 'Phone',
  in_person: 'In person',
  email: 'Email',
  waitlist: 'From the door',
  other: 'Other',
};

/** Still to come: can be confirmed, seated, cancelled or marked a no-show. */
export function isExpected(booking: Booking): boolean {
  return booking.status === 'booked' || booking.status === 'confirmed';
}

export interface Booking {
  id: string;
  location_id: string;
  status: BookingStatus;
  party_size: number;
  starts_at: string;
  ends_at: string;
  business_date: string;
  /** The arrival on the site's clock. Show these, never `starts_at` re-zoned. */
  local_date: string;
  local_time: string;
  duration_minutes: number;
  customer_id: string | null;
  /** From the guest's profile. Null once the guest has been erased. */
  guest_name: string | null;
  guest_phone: string | null;
  guest_email: string | null;
  guest_allergens: string[];
  guest_dietary: string | null;
  guest_notes: string | null;
  /** This evening's, for whoever is coming. */
  notes: string | null;
  allergens: string[];
  source: BookingSource;
  table_ids: string[];
  table_labels: string[];
  /** Seats across the tables held. */
  table_seats: number;
  waitlist_entry_id: string | null;
  /** The tab opened when they were seated. */
  order_id: string | null;
  confirmed_at: string | null;
  seated_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  no_show_at: string | null;
  confirmation_sent_at: string | null;
  deposit_required: string | null;
  deposit_received: string | null;
  deposit_received_at: string | null;
  deposit_note: string | null;
  created_at: string;
}

export interface BookingTable {
  id: string;
  label: string;
  seats: number;
  floor_plan_id: string;
  plan_name: string;
}

export interface BookingOpening {
  /** Monday is 0. */
  weekday: number;
  /** "18:00:00" — the site's clock. */
  first_arrival: string;
  last_arrival: string;
}

export interface BookingDay {
  location_id: string;
  business_date: string;
  openings: BookingOpening[];
  /** False when the site has set no booking hours at all (any time is taken). */
  hours_set: boolean;
  covers: number;
  bookings: Booking[];
  tables: BookingTable[];
}

export interface BookingSlot {
  starts_at: string;
  local_time: string;
  covers_booked: number;
  covers_cap: number | null;
  full: boolean;
  table_id: string | null;
  table_label: string | null;
}

export interface TurnTime {
  /** The biggest party this covers; null for any bigger party. */
  up_to: number | null;
  minutes: number;
}

export interface BookingSettings {
  location_id: string;
  /** False until a manager saves: these are the defaults. */
  saved: boolean;
  slot_minutes: number;
  covers_per_slot: number | null;
  turn_times: TurnTime[];
  deposit_from_party: number | null;
  deposit_per_cover: string | null;
  send_confirmations: boolean;
  hours: BookingOpening[];
}

export interface BookingSettingsInput {
  slot_minutes: number;
  covers_per_slot: number | null;
  turn_times: TurnTime[];
  deposit_from_party: number | null;
  deposit_per_cover: string | null;
  send_confirmations: boolean;
  hours: { weekday: number; first_arrival: string; last_arrival: string }[];
}

export interface BookingInput {
  location_id: string;
  party_size: number;
  /** YYYY-MM-DD and HH:MM on the site's clock. */
  date: string;
  time: string;
  /** A guest on file, or `guest` for somebody new. */
  customer_id?: string;
  guest?: { name: string; phone?: string | null; email?: string | null };
  /** Omitted: the smallest free table that fits. Empty: leave it for later. */
  table_ids?: string[];
  notes?: string | null;
  allergens?: string[];
  source?: BookingSource;
  /** Omitted: the site's rule. Null: none. */
  deposit_required?: string | null;
  send_confirmation?: boolean;
  /** Outside the hours, over a slot's covers, too small a table, in the past. */
  override?: boolean;
}

export interface BookingChange {
  party_size?: number;
  date?: string;
  time?: string;
  table_ids?: string[];
  notes?: string | null;
  allergens?: string[];
  deposit_required?: string | null;
  override?: boolean;
}

/**
 * Whether a refusal is one of the restaurant's own rules that the person may
 * book past: outside the hours, a full slot, a small table, a time gone.
 * A table held by another party is never one of these.
 */
export function canOverride(error: ApiError | null | undefined): boolean {
  return Boolean(error?.body?.['can_override']);
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['reservations'] });
    // Seating opens a tab and seats a table; a booking from the door takes
    // a party off the waitlist.
    void queryClient.invalidateQueries({ queryKey: ['floor'] });
    void queryClient.invalidateQueries({ queryKey: ['orders'] });
  };
}

/**
 * One trading day's bookings at a site, with its tables. Today on the site's
 * clock when no date is given. Refetched on an interval because the door and
 * the phone change it without this screen being touched.
 */
export function useBookingDay(
  locationId: string | undefined,
  date?: string,
  { enabled = true } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: ['reservations', 'day', locationId ?? null, date ?? 'today'],
    queryFn: ({ signal }) =>
      api.get<BookingDay>(
        `/v1/reservations?location_id=${locationId}${date ? `&date=${date}` : ''}`,
        signal,
      ),
    enabled: enabled && Boolean(locationId),
    staleTime: 10_000,
    refetchInterval: 60_000,
  });
}

/**
 * One booking, wherever it now is. Read by a screen holding a booking open
 * while it is moved to another day, which takes it off the day it came from.
 */
export function useBooking(id: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['reservations', 'one', id ?? null],
    queryFn: ({ signal }) => api.get<Booking>(`/v1/reservations/${id}`, signal),
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

/** The times offered on a day, how full each is, and the table a party would get. */
export function useBookingSlots(
  locationId: string | undefined,
  date: string,
  partySize: number,
  excludeId?: string,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['reservations', 'slots', locationId ?? null, date, partySize, excludeId ?? null],
    queryFn: ({ signal }) =>
      api.get<BookingSlot[]>(
        `/v1/reservations/availability?location_id=${locationId}&date=${date}` +
          `&party_size=${partySize}${excludeId ? `&exclude_id=${excludeId}` : ''}`,
        signal,
      ),
    enabled: Boolean(locationId) && Boolean(date) && partySize > 0,
    staleTime: 10_000,
  });
}

export function useBookingSettings(locationId: string | undefined, { enabled = true } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['reservations', 'settings', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<BookingSettings>(`/v1/reservations/settings?location_id=${locationId}`, signal),
    enabled: enabled && Boolean(locationId),
    staleTime: 60_000,
  });
}

export function useSaveBookingSettings() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<BookingSettings, ApiError, { locationId: string } & BookingSettingsInput>({
    mutationFn: ({ locationId, ...body }) =>
      api.request<BookingSettings>(`/v1/reservations/settings/${locationId}`, {
        method: 'PUT',
        body,
      }),
    onSettled: invalidate,
  });
}

export function useTakeBooking() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<Booking, ApiError, BookingInput>({
    mutationFn: (input) => api.post<Booking>('/v1/reservations', input),
    onSettled: invalidate,
  });
}

/** Only what is sent changes. Tables are checked again when the time moves. */
export function useChangeBooking() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<Booking, ApiError, { id: string } & BookingChange>({
    mutationFn: ({ id, ...body }) =>
      api.request<Booking>(`/v1/reservations/${id}`, { method: 'PATCH', body }),
    onSettled: invalidate,
  });
}

function useBookingAction<TInput extends { id: string }>(
  path: string,
  body: (input: TInput) => object | undefined = () => undefined,
) {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<Booking, ApiError, TInput>({
    mutationFn: (input) =>
      api.post<Booking>(`/v1/reservations/${input.id}/${path}`, body(input)),
    onSettled: invalidate,
  });
}

export function useConfirmBooking() {
  return useBookingAction<{ id: string }>('confirm');
}

/** Their tables are free again. `tellGuest` emails them when they gave an address. */
export function useCancelBooking() {
  return useBookingAction<{ id: string; reason?: string; tellGuest?: boolean }>(
    'cancel',
    ({ reason, tellGuest }) => ({
      ...(reason ? { reason } : {}),
      tell_guest: Boolean(tellGuest),
    }),
  );
}

export function useMarkNoShow() {
  return useBookingAction<{ id: string }>('no-show');
}

/** Back from cancelled or no-show. Comes back without tables given away meanwhile. */
export function useReinstateBooking() {
  return useBookingAction<{ id: string; override?: boolean }>('reinstate', ({ override }) => ({
    override: Boolean(override),
  }));
}

/** A deposit taken by the restaurant's own means. A record, never a payment. */
export function useRecordDeposit() {
  return useBookingAction<{ id: string; amount: string; note?: string }>(
    'deposit',
    ({ amount, note }) => ({ amount, ...(note ? { note } : {}) }),
  );
}

/** Email the guest their booking now. 409 without an address, 502 when mail fails. */
export function useEmailBooking() {
  return useBookingAction<{ id: string }>('email');
}

/**
 * The party is here: open the tab on their table with the guest on it.
 * Answers with the tab's id, for the till to go straight to it.
 */
export function useSeatBooking() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<
    { booking: Booking; order_id: string },
    ApiError,
    { id: string; tableIds?: string[] }
  >({
    mutationFn: ({ id, tableIds }) =>
      api.post<{ booking: Booking; order_id: string }>(
        `/v1/reservations/${id}/seat`,
        tableIds && tableIds.length > 0 ? { table_ids: tableIds } : {},
      ),
    onSettled: invalidate,
  });
}

/** A party at the door who will not wait: book them for later, off the list as booked. */
export function useBookFromWaitlist() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<
    Booking,
    ApiError,
    {
      entryId: string;
      date: string;
      time: string;
      email?: string;
      partySize?: number;
      override?: boolean;
    }
  >({
    mutationFn: ({ entryId, date, time, email, partySize, override }) =>
      api.post<Booking>(`/v1/reservations/from-waitlist/${entryId}`, {
        date,
        time,
        ...(email ? { email } : {}),
        ...(partySize ? { party_size: partySize } : {}),
        override: Boolean(override),
      }),
    onSettled: invalidate,
  });
}
